'use client';

import { useEffect } from 'react';

/**
 * Toggles a `data-stuck` attribute on a `position: sticky` element while it is
 * pinned at its sticky offset (i.e. content is scrolling beneath it).
 *
 * This lets CSS give a pinned toolbar elevation that depends only on whether the
 * bar is floating over content — not on scroll direction. Tying the shadow to
 * scroll direction (or to the header's hidden state) makes it blink off the
 * instant the user scrolls up, even though the bar is still pinned.
 *
 * Detection compares the element's viewport position to its resolved sticky
 * `top`: once the element has reached that offset it is stuck. Reading the
 * computed `top` each tick keeps it correct even while that offset itself
 * animates (the anchor nav's offset changes as the header reveals/hides).
 */
export function useStuckAttribute(ref: { current: HTMLElement | null }) {
  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    let ticking = false;

    const update = () => {
      ticking = false;
      const top = el.getBoundingClientRect().top;
      const stickyTop = parseFloat(getComputedStyle(el).top) || 0;
      if (top <= stickyTop + 1) {
        el.setAttribute('data-stuck', '');
      } else {
        el.removeAttribute('data-stuck');
      }
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };

    // The element's sticky `top` animates (the header reveal swaps the offset),
    // and that animation can finish *after* the scroll gesture stops — leaving
    // no scroll event to re-evaluate against the settled geometry. Re-check once
    // the `top` transition ends so `data-stuck` can't be left stale.
    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.propertyName === 'top') {
        update();
      }
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    el.addEventListener('transitionend', onTransitionEnd);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      el.removeEventListener('transitionend', onTransitionEnd);
      el.removeAttribute('data-stuck');
    };
  }, [ref]);
}
